// Copyright 2026 - Hewlett Packard Enterprise Company

// Build-time constant injected by Vite (see vite.config.js)
/* global __PRODUCT__ */
const PRODUCT = __PRODUCT__;

/**
 * Product capabilities configuration
 *
 * IMPORTANT: Use this approach ONLY as a last resort when:
 * - Both products use the same component AND
 * - Converting the component to accept configuration via props is not possible AND
 * - Product-specific behavior cannot be abstracted into a configurable pattern
 *
 * This centralized capability check should be preferred over scattered
 * product-specific conditionals (e.g., `PRODUCT === 'NUCLEON'`) throughout
 * the codebase. Always try to make components configurable first before
 * adding a new capability flag here.
 *
 * Benefits of this approach:
 * - Single source of truth for product differentiation
 * - Easier to maintain and understand product differences
 * - Prevents anti-pattern of scattered conditional checks
 * - Supports temporary compatibility fixes until product parity is available;
 *   these flags should be removed once the correct implementation is supported
 *
 */
export const PRODUCT_CAPABILITIES = {
  iloUpdateSupport: PRODUCT !== 'NUCLEON',
  groupSupport: PRODUCT !== 'NUCLEON',
  requiresIncludeAllDataOnServers: PRODUCT === 'NUCLEON',
  parentChildJobsSupport: PRODUCT !== 'NUCLEON',
  themePreferenceSupport: PRODUCT === 'COM',
  enforcePlatformFamilyEligibility: PRODUCT !== 'NUCLEON',
  applianceSupport: PRODUCT === 'COM',
  aiSupport: PRODUCT === 'COM',
  approvalPolicySupport: PRODUCT !== 'HELIX',
  allSettingCategoriesSupport: PRODUCT !== 'HELIX',
  schedulerSupport: PRODUCT !== 'NUCLEON',
  tagsSupport: PRODUCT !== 'HELIX',
  // External storage is never supported in Helix, even once
  // allSettingCategoriesSupport is eventually enabled there.
  externalStorageSupport: PRODUCT !== 'HELIX',
  // OverviewPageV2 subsystems Helix has no backend for; sustainability reuses aiSupport.
  mapViewSupport: PRODUCT !== 'HELIX',
  serviceNowSupport: PRODUCT !== 'HELIX',
  newFeatureAnnouncementSupport: PRODUCT !== 'HELIX',
  // Only COM supports the full server action set; Helix is limited to power
  // on/off, reset, cold boot, and update firmware, and Nucleon's DeviceTable
  // usage always supplies its own actionsOverride.
  extendedServerActionsSupport: PRODUCT === 'COM',
};
